/**
 * Smart Cache Manager with file-type-aware invalidation
 * 
 * This cache manager understands which file types affect which reports:
 * - Weekly reports: gfs, ezcater, ezcaterweekly, toast, toastweekly
 * - Quarterly reports: gfsquarterly, ezcaterquarterly, toastquarterly
 * - Both: All file types affect date ranges/quarters lists
 */

export type FileType = 
  | 'gfs' 
  | 'gfsquarterly' 
  | 'ezcater' 
  | 'ezcaterweekly' 
  | 'ezcaterquarterly' 
  | 'toast' 
  | 'toastweekly' 
  | 'toastquarterly';

export type ReportType = 'weekly' | 'quarterly' | 'dateRanges' | 'quarters' | 'expenses' | 'sales';

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresAt: number;
  reportTypes: Set<ReportType>; // Which report types this cache entry is for
}

interface CacheConfig {
  ttl: number; // Time to live in milliseconds
  reportTypes: ReportType[]; // Which report types this cache key is relevant for
}

// Map file types to the report types they affect
const FILE_TYPE_TO_REPORT_TYPES: Record<FileType, ReportType[]> = {
  // Weekly files affect weekly reports and date ranges
  gfs: ['weekly', 'dateRanges'],
  ezcater: ['weekly', 'dateRanges'],
  ezcaterweekly: ['weekly', 'dateRanges'],
  toast: ['weekly', 'dateRanges'],
  toastweekly: ['weekly', 'dateRanges'],
  
  // Quarterly files affect quarterly reports and quarters list
  gfsquarterly: ['quarterly', 'quarters', 'expenses'],
  ezcaterquarterly: ['quarterly', 'quarters'],
  toastquarterly: ['quarterly', 'quarters'],
};

// Cache configurations for different endpoint patterns
const CACHE_CONFIGS = new Map<string | RegExp, CacheConfig>([
  // Dashboard weekly data - long TTL since we invalidate on file changes
  ['/api/dashboard/data', { ttl: 60 * 60 * 1000, reportTypes: ['weekly'] as ReportType[] }], // 1 hour
  // Dashboard quarterly data
  ['/api/dashboard/quarterly-data', { ttl: 60 * 60 * 1000, reportTypes: ['quarterly'] as ReportType[] }], // 1 hour
  // Date ranges - long TTL, invalidated on weekly file changes
  ['/api/dashboard/date-ranges', { ttl: 60 * 60 * 1000, reportTypes: ['dateRanges'] as ReportType[] }], // 1 hour
  // Quarters list - long TTL, invalidated on quarterly file changes
  ['/api/dashboard/quarters', { ttl: 60 * 60 * 1000, reportTypes: ['quarters'] as ReportType[] }], // 1 hour
  // Data status - shorter TTL but still cacheable
  [/\/api\/dashboard\/data-status/, { ttl: 10 * 60 * 1000, reportTypes: ['weekly'] as ReportType[] }], // 10 minutes
  [/\/api\/dashboard\/quarterly-data-status/, { ttl: 10 * 60 * 1000, reportTypes: ['quarterly'] as ReportType[] }], // 10 minutes
  // File lists - cache with smart invalidation
  [/\/api\/files\/type/, { ttl: 30 * 60 * 1000, reportTypes: [] as ReportType[] }], // 30 minutes
  // Expenses - cache expense data
  [/\/api\/expenses\/data/, { ttl: 60 * 60 * 1000, reportTypes: ['expenses'] as ReportType[] }], // 1 hour
  [/\/api\/expenses\/quarterly/, { ttl: 60 * 60 * 1000, reportTypes: ['expenses', 'quarterly'] as ReportType[] }], // 1 hour
  // Sales data - cache with medium TTL
  [/\/api\/sales/, { ttl: 30 * 60 * 1000, reportTypes: ['sales'] as ReportType[] }], // 30 minutes
] as Array<[string | RegExp, CacheConfig]>);

class CacheManager {
  private cache: Map<string, CacheEntry> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private listeners: Set<(cacheKey: string) => void> = new Set();

  /**
   * Get cache configuration for a URL
   */
  private getCacheConfig(url: string): CacheConfig | null {
    // Try exact match first
    if (CACHE_CONFIGS.has(url)) {
      return CACHE_CONFIGS.get(url)!;
    }
    
    // Try regex match
    for (const [pattern, config] of CACHE_CONFIGS.entries()) {
      if (pattern instanceof RegExp && pattern.test(url)) {
        return config;
      }
      if (typeof pattern === 'string' && url.includes(pattern)) {
        return config;
      }
    }
    
    // Default: no caching
    return null;
  }

  /**
   * Generate cache key from URL and params
   */
  generateCacheKey(url: string, params?: any): string {
    const userId = localStorage.getItem('userId') || 'anonymous';
    const paramsStr = params ? JSON.stringify(params) : '';
    return `${userId}:${url}:${paramsStr}`;
  }

  /**
   * Get data from cache
   */
  get(cacheKey: string): any | null {
    const entry = this.cache.get(cacheKey);
    if (!entry) return null;

    const now = Date.now();
    if (now > entry.expiresAt) {
      this.cache.delete(cacheKey);
      return null;
    }

    return entry.data;
  }

  /**
   * Set data in cache
   */
  set(cacheKey: string, data: any, url: string): void {
    const config = this.getCacheConfig(url);
    if (!config) {
      // No caching configured for this URL
      return;
    }

    const now = Date.now();
    this.cache.set(cacheKey, {
      data,
      timestamp: now,
      expiresAt: now + config.ttl,
      reportTypes: new Set(config.reportTypes),
    });
  }

  /**
   * Invalidate cache entries affected by file type changes
   */
  invalidateByFileType(fileType: FileType): void {
    const affectedReportTypes = FILE_TYPE_TO_REPORT_TYPES[fileType] || [];
    
    if (affectedReportTypes.length === 0) {
      return;
    }

    const reportTypeSet = new Set(affectedReportTypes);
    let invalidatedCount = 0;

    // Invalidate all cache entries that match affected report types
    for (const [key, entry] of this.cache.entries()) {
      // Check if this entry is relevant to any affected report type
      const hasAffectedType = Array.from(entry.reportTypes).some(rt => reportTypeSet.has(rt));
      
      if (hasAffectedType) {
        this.cache.delete(key);
        invalidatedCount++;
        // Notify listeners
        this.notifyListeners(key);
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[CacheManager] Invalidated ${invalidatedCount} cache entries for file type: ${fileType} (affects: ${affectedReportTypes.join(', ')})`);
    }
  }

  /**
   * Invalidate all cache entries for specific report types
   */
  invalidateByReportType(reportTypes: ReportType[]): void {
    const reportTypeSet = new Set(reportTypes);
    let invalidatedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      const hasAffectedType = Array.from(entry.reportTypes).some(rt => reportTypeSet.has(rt));
      
      if (hasAffectedType) {
        this.cache.delete(key);
        invalidatedCount++;
        this.notifyListeners(key);
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[CacheManager] Invalidated ${invalidatedCount} cache entries for report types: ${reportTypes.join(', ')}`);
    }
  }

  /**
   * Invalidate a specific cache key
   */
  invalidate(cacheKey: string): void {
    if (this.cache.has(cacheKey)) {
      this.cache.delete(cacheKey);
      this.notifyListeners(cacheKey);
    }
  }

  /**
   * Clear all cache
   */
  clear(): void {
    const keys = Array.from(this.cache.keys());
    this.cache.clear();
    this.pendingRequests.clear();
    keys.forEach(key => this.notifyListeners(key));
  }

  /**
   * Get or set pending request (for deduplication)
   */
  getPendingRequest(cacheKey: string): Promise<any> | null {
    return this.pendingRequests.get(cacheKey) || null;
  }

  setPendingRequest(cacheKey: string, promise: Promise<any>): void {
    this.pendingRequests.set(cacheKey, promise);
    promise.finally(() => {
      this.pendingRequests.delete(cacheKey);
    });
  }

  /**
   * Add cache invalidation listener
   */
  addListener(listener: (cacheKey: string) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of cache invalidation
   */
  private notifyListeners(cacheKey: string): void {
    this.listeners.forEach(listener => {
      try {
        listener(cacheKey);
      } catch (error) {
        console.error('[CacheManager] Listener error:', error);
      }
    });
  }

  /**
   * Get cache stats (for debugging)
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Export helper to get affected report types for a file type
export function getReportTypesForFileType(fileType: FileType): ReportType[] {
  return FILE_TYPE_TO_REPORT_TYPES[fileType] || [];
}

